import { useCallback, useEffect, useMemo, useState } from 'react';
import { ulid } from 'ulid';
import {
  buildSchema,
  computeSectionProgress,
  visibleQuestions,
} from '@zg/shared/property';
import type {
  Answers,
  BuiltSchema,
  Classifier,
  Question,
  SectionId,
} from '@zg/shared/property';
import {
  loadAnswers,
  loadClassifier,
  loadDraftId,
  saveAnswers,
  saveDraftId,
} from '../lib/propertyStorage';

export interface SubmissionPayload {
  draftId: string;
  classifier: Classifier;
  answers: Answers;
  submittedAt: string;
}

export interface UsePropertyQuoteFormResult {
  classifier: Classifier | null;
  schema: BuiltSchema | null;
  answers: Answers;
  draftId: string;
  visibleQuestionsFor: (sectionId: SectionId) => Question[];
  visibleQuestionIds: Set<string>;
  setAnswer: (id: string, value: unknown) => void;
  errors: Record<string, string>;
  validateSection: (sectionId: SectionId) => string[]; // returns list of field ids with errors
  sectionProgress: ReturnType<typeof computeSectionProgress>;
  submit: () => SubmissionPayload | null;
}

/**
 * State machine for the adaptive property-quote form.
 *
 * - Reads the classifier from sessionStorage (set by the classifier screen).
 * - Hydrates answers from sessionStorage on mount; persists back on change.
 * - Exposes per-section validation (uses `visibleQuestions` to only require
 *   what's actually on screen).
 * - In-memory submit: bundles draftId + classifier + answers into a
 *   `SubmissionPayload`. Real API wiring lands in P-6/P-8.
 */
export function usePropertyQuoteForm(): UsePropertyQuoteFormResult {
  const classifier = useMemo(() => loadClassifier(), []);
  const schema = useMemo<BuiltSchema | null>(
    () => (classifier ? buildSchema(classifier) : null),
    [classifier]
  );

  const [answers, setAnswers] = useState<Answers>(() => loadAnswers());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [draftId] = useState<string>(() => {
    const existing = loadDraftId();
    if (existing) return existing;
    const fresh = ulid();
    saveDraftId(fresh);
    return fresh;
  });

  useEffect(() => {
    saveAnswers(answers);
  }, [answers]);

  const visibleQs: Question[] = useMemo(() => {
    if (!schema || !classifier) return [];
    return visibleQuestions(schema, classifier, answers);
  }, [schema, classifier, answers]);

  const visibleQuestionIds = useMemo(
    () => new Set(visibleQs.map((q) => q.id)),
    [visibleQs]
  );

  const visibleQuestionsFor = useCallback(
    (sectionId: SectionId) => visibleQs.filter((q) => q.section === sectionId),
    [visibleQs]
  );

  const setAnswer = useCallback((id: string, value: unknown) => {
    setAnswers((prev) => {
      if (value === undefined || value === null || value === '') {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: value };
    });
    setErrors((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const validateSection = useCallback(
    (sectionId: SectionId): string[] => {
      const sectionQs = visibleQs.filter((q) => q.section === sectionId);
      const missing: string[] = [];
      const nextErrors: Record<string, string> = { ...errors };
      for (const q of sectionQs) {
        if (!q.required) continue;
        const v = answers[q.id];
        if (!hasAnswer(v)) {
          missing.push(q.id);
          nextErrors[q.id] = 'Required';
        } else {
          delete nextErrors[q.id];
        }
      }
      setErrors(nextErrors);
      return missing;
    },
    [visibleQs, answers, errors]
  );

  const sectionProgress = useMemo(() => {
    if (!schema || !classifier)
      return [] as ReturnType<typeof computeSectionProgress>;
    return computeSectionProgress(schema, classifier, answers);
  }, [schema, classifier, answers]);

  const submit = useCallback((): SubmissionPayload | null => {
    if (!classifier) return null;
    // Validate every required visible question across every section.
    const missing: string[] = [];
    for (const q of visibleQs) {
      if (q.required && !hasAnswer(answers[q.id])) missing.push(q.id);
    }
    if (missing.length > 0) {
      const next: Record<string, string> = { ...errors };
      for (const id of missing) next[id] = 'Required';
      setErrors(next);
      return null;
    }
    return {
      draftId,
      classifier,
      answers,
      submittedAt: new Date().toISOString(),
    };
  }, [classifier, visibleQs, answers, draftId, errors]);

  return {
    classifier,
    schema,
    answers,
    draftId,
    visibleQuestionsFor,
    visibleQuestionIds,
    setAnswer,
    errors,
    validateSection,
    sectionProgress,
    submit,
  };
}

function hasAnswer(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value as object).length > 0;
  return true;
}
